import jwtDecode from "jwt-decode";
import axiosInstance from "./axiosInstance";
import { backendbaseUrl } from "./baseUrl";

/**
 * Literal-valued object so `RedirectReason` (authRedirect.ts) can spread these
 * values while keeping their exact string types — a plain const would widen
 * each member to `string` and break the derived union.
 * @type {{ readonly missing: "missing", readonly malformed: "malformed", readonly missing_user_id: "missing_user_id", readonly invalid_user_id: "invalid_user_id", readonly expired: "expired", readonly wrong_token_type: "wrong_token_type" }}
 */
export const VALIDATION_REASONS = {
  missing: "missing",
  malformed: "malformed",
  missing_user_id: "missing_user_id",
  invalid_user_id: "invalid_user_id",
  expired: "expired",
  wrong_token_type: "wrong_token_type",
};

const CLOCK_SKEW_MS = 60 * 1000;

/**
 * Validate token and extract userId without side effects.
 * @param {string | null | undefined} token
 * @returns {{ ok: true, userId: string } | { ok: false, reason: ValidationReason }}
 */
export const getValidatedUserId = (token) => {
  if (!token) return { ok: false, reason: VALIDATION_REASONS.missing };
  let decoded;
  try {
    decoded = jwtDecode(token);
  } catch (e) {
    return { ok: false, reason: VALIDATION_REASONS.malformed };
  }
  const userId = decoded.user_id;
  if (userId === undefined || userId === null || userId === "") {
    return { ok: false, reason: VALIDATION_REASONS.missing_user_id };
  }
  const userIdStr = String(userId).trim();
  if (!userIdStr || userIdStr === "undefined" || userIdStr === "null") {
    return { ok: false, reason: VALIDATION_REASONS.invalid_user_id };
  }
  const exp = decoded.exp;
  if (typeof exp === "number") {
    if (Date.now() >= exp * 1000 - CLOCK_SKEW_MS) {
      return { ok: false, reason: VALIDATION_REASONS.expired };
    }
  }
  const tokenType = decoded.token_type;
  if (tokenType !== undefined && tokenType !== null && tokenType !== "access") {
    return { ok: false, reason: VALIDATION_REASONS.wrong_token_type };
  }
  return { ok: true, userId: userIdStr };
};

export const getAccessToken = () => localStorage.getItem("accessToken");
export const getRefreshToken = () => localStorage.getItem("refreshToken");

export const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);
};

/** @type {() => void} */
export const clearTokens = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("resourceList");
};

// Function to check if token is expired
export const isTokenValid = (token) => {
  if (!token) return false;

  try {
    const { exp } = jwtDecode(token);
    return Date.now() < exp * 1000; // Convert expiration time to milliseconds
  } catch (error) {
    return false;
  }
};

// Function to refresh access token
export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const response = await axiosInstance.post(
      `${backendbaseUrl}/api/token/refresh/`,
      { refresh: refreshToken }
    );
    const newAccessToken = response.data.access;

    setTokens(newAccessToken, refreshToken);
    return newAccessToken;
  } catch (error) {
    console.error("Failed to refresh token", error);
    clearTokens();
    return null;
  }
};
